import React, { useMemo } from "react";
import PeriodSelector from "./PeriodSelector";
import CustomDateRangePicker from "./CustomDateRangePicker";
import Button from "./Buttons";

import "./TotalAnalytics.css";

const PostCallAnalyticsSummary = ({
  summary,
  loading,
  numericMetrics = [],
  categoricalMetrics = [],
  selectedPeriod,
  onPeriodChange,
  customFrom,
  customTo,
  setCustomFrom,
  setCustomTo,
  onRefresh
}) => {
  const hasTrackableMetrics =
    (numericMetrics?.length || 0) > 0 || (categoricalMetrics?.length || 0) > 0;

  const numericRows = useMemo(() => summary?.numeric || [], [summary]);
  const categoricalRows = useMemo(() => summary?.categorical || [], [summary]);

  const formatValue = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "number") return v.toLocaleString();
    return String(v);
  };

  const getSortedCategoryEntries = (counts) => {
    return Object.entries(counts || {}).sort((a, b) => (b[1] || 0) - (a[1] || 0));
  };

  // === Numeric "Min/Max/Avg" in a column with lighter labels
  const renderNumericTriplet = (m) => {
    const labelStyle = { fontWeight: 500, opacity: 0.9 };

    return (
      <span
        className="value"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          alignItems: "flex-end",
          textAlign: "right"
        }}
      >
        <span>
          <span style={labelStyle}>Min</span> {formatValue(m.min)}
        </span>
        <span>
          <span style={labelStyle}>Max</span> {formatValue(m.max)}
        </span>
        <span>
          <span style={labelStyle}>Avg</span> {formatValue(m.avg)}
        </span>
      </span>
    );
  };

  // === Categorical stats (card): Total + top categories in a column
  const renderTopCategoriesColumn = (counts, totalChats, topN = 2) => {
    const entries = getSortedCategoryEntries(counts);
    const top = entries.slice(0, topN);
    const restCount = Math.max(0, entries.length - topN);

    return (
      <div
        className="value"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          alignItems: "flex-end",
          textAlign: "right"
        }}
      >
        <div style={{ fontWeight: 500, opacity: 0.9 }}>
          Total chats: {formatValue(totalChats)}
        </div>

        {top.length === 0 ? (
          <div style={{ opacity: 0.8 }}>no data</div>
        ) : (
          <>
            {top.map(([k, v]) => (
              <div key={k}>
                {k}: {formatValue(v)}
              </div>
            ))}
            {restCount > 0 && (
              <div className="small-font" style={{ opacity: 0.8 }}>
                +{restCount} more
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // === Distribution as column (for tables); trims long lists
  const renderDistributionColumn = (counts, maxItems = 6) => {
    const entries = getSortedCategoryEntries(counts);

    if (entries.length === 0) {
      return <span>—</span>;
    }

    const visible = entries.slice(0, maxItems);
    const rest = entries.length - maxItems;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {visible.map(([k, v]) => (
          <div key={k}>
            {k}: {formatValue(v)}
          </div>
        ))}
        {rest > 0 && (
          <div className="small-font" style={{ opacity: 0.8 }}>
            +{rest} more
          </div>
        )}
      </div>
    );
  };

  const renderChatsCell = (value) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "2px"
        }}
      >
        <div className="small-font" style={{ opacity: 0.8 }}>
          Total
        </div>
        <div>{(value || 0).toLocaleString()}</div>
      </div>
    );
  };

  // === Small table cosmetics: separators + better column spacing (no design change)
  const rowSeparatorColor = "rgba(0,0,0,0.06)";
  const cellPaddingY = "14px";

  const chatsColStyle = {
    textAlign: "right",
    verticalAlign: "top",
    paddingRight: "22px",
    paddingTop: cellPaddingY,
    paddingBottom: cellPaddingY,
    width: "160px"
  };

  const distributionColStyle = {
    verticalAlign: "top",
    paddingLeft: "22px",
    paddingTop: cellPaddingY,
    paddingBottom: cellPaddingY,
    borderLeft: `1px solid ${rowSeparatorColor}`
  };

  const metricColStyle = {
    verticalAlign: "top",
    paddingTop: cellPaddingY,
    paddingBottom: cellPaddingY
  };

  return (
    <div className="radius-yes p-2 bg-yes col-12">
      <div className="d-flex flex-row justify-between align-center">
        <h2 className="mb-4">Post-Call Analytics</h2>

        <Button
          variant="secondary"
          size="medium"
          onClick={onRefresh}
          disabled={loading}
        >
          <i className="fa-solid fa-rotate-right"></i> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="small-font">Loading post-call analytics…</div>
      ) : !hasTrackableMetrics ? (
        <div className="small-font">
          No analytics summary available. Add at least one <b>numeric</b> or{" "}
          <b>categorical</b> metric.
        </div>
      ) : !summary ? (
        <div className="small-font">No summary data (stub/backend not ready).</div>
      ) : (
        <>
          {/* Cards */}
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="analytics-header">
                <h3>Total Chats with analytics</h3>
              </div>
              <div className="analytics-value">
                {summary.chats_with_analytics?.toLocaleString() || 0}
              </div>
              <div className="analytics-details">
                <div className="detail-item">
                  <span className="label">Numeric metrics</span>
                  <span className="value">{numericRows.length}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Categorical metrics</span>
                  <span className="value">{categoricalRows.length}</span>
                </div>
              </div>
            </div>

            <div className="analytics-card">
              <div className="analytics-header">
                <h3>Numeric stats</h3>
              </div>
              <div className="analytics-details">
                {numericRows.slice(0, 3).map((m) => (
                  <div key={m.metric_key} className="detail-item">
                    <span className="label">{m.display_name}</span>
                    {renderNumericTriplet(m)}
                  </div>
                ))}
                {numericRows.length > 3 && (
                  <div className="small-font" style={{ opacity: 0.8 }}>
                    +{numericRows.length - 3} more
                  </div>
                )}
              </div>
            </div>

            <div className="analytics-card">
              <div className="analytics-header">
                <h3>Categorical stats</h3>
              </div>
              <div className="analytics-details">
                {categoricalRows.slice(0, 3).map((m) => (
                  <div key={m.metric_key} className="detail-item">
                    <span className="label">{m.display_name}</span>
                    {renderTopCategoriesColumn(m.counts, m.chats_with_value, 2)}
                  </div>
                ))}
                {categoricalRows.length > 3 && (
                  <div className="small-font" style={{ opacity: 0.8 }}>
                    +{categoricalRows.length - 3} more
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Numeric table */}
          {numericRows.length > 0 && (
            <div className="mt-2">
              <h3 className="h3-title">Numeric metrics</h3>
              <div className="small-font mb-1">
                For each numeric metric: chats count, min, max, average.
              </div>

              <div className="border-yes radius-yes p-2">
                <table className="table w-100">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Metric</th>
                      <th style={{ textAlign: "right" }}>Chats</th>
                      <th style={{ textAlign: "right" }}>Min</th>
                      <th style={{ textAlign: "right" }}>Max</th>
                      <th style={{ textAlign: "right" }}>Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {numericRows.map((m) => (
                      <tr key={m.metric_key}>
                        <td>{m.display_name}</td>
                        <td style={{ textAlign: "right" }}>
                          {(m.chats_with_value || 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>{formatValue(m.min)}</td>
                        <td style={{ textAlign: "right" }}>{formatValue(m.max)}</td>
                        <td style={{ textAlign: "right" }}>{formatValue(m.avg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Categorical table */}
          {categoricalRows.length > 0 && (
            <div className="mt-2">
              <h3 className="h3-title">Categorical metrics</h3>
              <div className="small-font mb-1">
                For each categorical metric: number of chats for each category.
              </div>

              <div className="border-yes radius-yes p-2">
                <table
                  className="table w-100"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Metric</th>
                      <th style={{ textAlign: "right", width: "160px", paddingRight: "22px" }}>
                        Chats
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          paddingLeft: "22px",
                          borderLeft: `1px solid ${rowSeparatorColor}`
                        }}
                      >
                        Chats Distribution
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoricalRows.map((m, idx) => (
                      <tr
                        key={m.metric_key}
                        style={{
                          borderBottom:
                            idx === categoricalRows.length - 1
                              ? "none"
                              : `1px solid ${rowSeparatorColor}`
                        }}
                      >
                        <td style={metricColStyle}>{m.display_name}</td>
                        <td style={chatsColStyle}>{renderChatsCell(m.chats_with_value)}</td>
                        <td style={distributionColStyle}>
                          {renderDistributionColumn(m.counts, 6)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* period selector */}
      <div style={{ marginTop: "6vh" }}>
        <PeriodSelector selectedPeriod={selectedPeriod} onChange={onPeriodChange} />
        {selectedPeriod === "custom" && (
          <CustomDateRangePicker
            customFrom={customFrom}
            customTo={customTo}
            setCustomFrom={setCustomFrom}
            setCustomTo={setCustomTo}
          />
        )}
      </div>
    </div>
  );
};

export default PostCallAnalyticsSummary;
